import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  OnInit,
} from '@angular/core';
import { AppState } from '@core/constants/app-state.enum';
import {
  DashboardAnnouncedItemView,
  DashboardBlockState,
  DashboardFailedIntegration,
  DashboardPortalCardView,
  DashboardTotals,
  Portal,
} from '@core/models';
import { getAngularService } from '../../ng-upgrade.utils';
import { DashboardService } from '../../services/dashboard.service';
import { NavigationService } from '../../services/navigation.service';
import { VehicleService } from '../../services/vehicle.service';

// Default de itens listados nos blocos 3/5 (DASHBOARD_LIST_LIMIT) — premissa marcada no
// PLAN §5, a confirmar contra a fonte visual aprovada. Única declaração: TASK-002-004
// reutiliza esta constante, não cria uma segunda.
export const DASHBOARD_LIST_LIMIT = 5;

// Superfície de card compartilhada pelos blocos que ainda usam a superfície completa
// (achado de design #2) — fundo, borda, raio e padding consistentes, sem sombra;
// `relative` ancora o overlay do spinner (achado #1), `min-h-[120px]` evita o colapso do
// bloco enquanto os dados ainda não chegaram. A section de KPIs não usa mais esta
// constante inteira (perdeu o card externo): `kpisSectionClass` abaixo é uma classe fixa
// (`relative`) — o estado `loading` renderiza 4 skeletons individuais no mesmo grid do
// estado `success` (BRIEF-010); nenhum estado da section usa overlay `absolute`, então
// `min-h-[120px]` não é necessário em nenhum deles.
//
// `border-[1px]`, `p-[1rem]`, `mt-[1.25rem]` e `mb-[1rem]` neste arquivo usam valor
// arbitrário em vez da utility bare equivalente do Tailwind (respectivamente: a utility de
// borda sem sufixo, a utility de padding nível 4, a utility de margem-topo nível 5 e a
// utility de margem-base nível 4) porque o CSS legado (assets/css/style.comp.css) declara
// essas classes com !important e valor diferente do pretendido — ver BRIEF-004. O guard
// src-angular/app/design-system/legacy-css-collisions.spec.ts garante que nenhum utility
// bare colidente volte a este componente. Espaçamento em rem (não px) preserva a escala
// de acessibilidade (zoom de fonte do navegador); border-[1px] fica em px porque largura
// de borda não escala com rem por convenção. `mb-[4px]` e `mb-[18px]` (bloco
// portals-to-contract) ficam em px porque são o valor literal do protótipo aprovado
// (BRIEF-005), não por falta de equivalente em rem. O guard só tokeniza atributos
// `class="..."` literais e constantes TS (como esta) — classes ligadas via
// [ngClass]/[class] com string inline não são varridas; revisão manual continua
// necessária para essas.
//
// Os `<h2>` de título de card (portal-counts, failed-ads, recent-ads,
// portals-to-contract) e o `<p>` de subtítulo de portals-to-contract usam margem
// direcional (utility `mb` com valor arbitrário entre colchetes) sem `!important` — por
// isso nenhum deles pode carregar `m-0`
// junto (`.m-0{margin:0!important}` do legado venceria e zeraria o espaçamento; é a
// recorrência documentada em guidelines/project/lessons.md). Os `<h2>` de failed-ads e
// recent-ads (cada um dentro do próprio wrapper flex com o botão "Ver na lista",
// TASK-008-001/FR-007-012) não têm margem própria — a margem do bloco vive no wrapper —
// por isso são os únicos desse grupo que mantêm `m-0`.
//
// `text-[15px] font-bold leading-[32px]` (3 utilities Tailwind isoladas, cada uma numa
// única propriedade CSS) substitui `text-aav-label-large-bold` nos 4 `<h2>` de título —
// DEC-008-004/FR-007-013 (TASK-008-001): o token antigo empacotava font-size/line-height/
// font-weight numa única classe sem variante de 32px de altura de linha, exigida para
// alinhar o título à altura do botão "Ver na lista" (`aav-button size="sm"`, 32px).
//
// A borda externa dos cards (6 pontos, valor arbitrário sobre a CSS var
// `--aav-neutral-200`) e o separador interno das 2 tabelas (4 pontos, token
// `border-aav-outline`) eram a mesma classe (`border-aav-outline-variant`, `#D4D4D4`) até
// a TASK-008-001 — o protótipo exige dois tons distintos (DEC-008-001/DEC-008-002). A cor
// arbitrária da coluna "Motivo" (`#B7231F`) não usa o token `aav-error` existente porque
// este resolve para `#B91C1C`, cor diferente da medida no protótipo (DEC-008-005). Pela
// mesma razão, as células de dado (`plate`/`portal`) não usam `text-aav-on-surface`
// (resolve para `#000000`, `--aav-on-surface: var(--aav-black)`), e sim o valor arbitrário
// `text-[rgb(var(--aav-stone-900))]` (`--aav-stone-900` = `#1C1917`, o valor medido —
// DEC-008-006, achado do gate 11 na wave de implementação).
const BLOCK_SURFACE_CLASS =
  'relative min-h-[120px] rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem]';

// Wrapper de centralização das mensagens de "sem dados"/erro de Anúncios com
// falha/Anúncios recentes (DEC-008-003, emendada no retry de TASK-008-001). A versão
// original pareava `min-h-[120px]` ao de `BLOCK_SURFACE_CLASS` "por comentário", mas os
// dois `120px` medem coisas diferentes (altura mínima do card inteiro vs. altura mínima só
// da área abaixo do título) — o card saltava de ~120px (loading) para ~202px (vazio/erro),
// e a mensagem ficava ancorada 120px abaixo do título, não centralizada na altura real do
// card. Corrigido: a `<section>` de failed-ads/recent-ads também é `flex flex-col`, cada um
// dos 3 estados de conteúdo (linhas/spinner/mensagem) recebe `flex-1` para ocupar o espaço
// restante da coluna, e este wrapper vira `flex-1` (sem `min-h` próprio) — a mensagem
// centraliza dentro da altura REAL do card, inclusive quando os 2 blocos ficam lado a lado
// e um deles estica por `align-items: stretch`.
const CARD_MESSAGE_WRAPPER_CLASS = 'flex flex-1 items-center justify-center';

// Grade do bloco portal-counts, compartilhada entre o skeleton (`loading`) e a lista real
// de `app-portal-card` (`success`) — mesma constante nos dois estados evita que uma
// mudança de geometria futura seja aplicada só num dos dois e desalinhe loading/success
// (ver comentário "Skeleton de lista de portal" abaixo para o histórico do valor).
const PORTAL_COUNTS_GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))]';

// Vitrine do bloco portals-to-contract, compartilhada entre o skeleton e a lista real de
// `app-portal-card` — mesmo motivo de `PORTAL_COUNTS_GRID_CLASS` acima (ver comentário
// "Skeleton de vitrine de logo" abaixo).
const PORTALS_TO_CONTRACT_SHOWCASE_CLASS = 'flex flex-wrap items-center gap-x-[28px] gap-y-[14px]';

// Skeleton de tabela (failed-ads/recent-ads, BRIEF-011, achado #1 do retry do
// product-designer) — geometria medida contra a estrutura real e estável dessas 2 tabelas
// (mesma classe de grade das linhas reais, `grid grid-cols-[1fr_1fr_2fr] gap-2 border-b
// border-aav-outline py-2`), sem cabeçalho de colunas (AC-007-006/007 não é reaberto aqui:
// o cabeçalho continua ausente em `loading`). `DASHBOARD_LIST_LIMIT` linhas — a mesma
// constante já usada como `limit` das 2 chamadas de API, não uma segunda declaração. Cada
// barra (`h-3.5`, 14px) fica dentro de um wrapper `h-4` (16px, mesmo padrão do valor de KPI
// em BRIEF-010, `mt-2 flex h-[35px] items-center` — aqui o alvo é a caixa de linha de
// `text-aav-body-small`, 16px, não os 35px do valor de KPI): sem o wrapper, a soma da linha
// skeleton (8 + 14 + 8 + 1 = 31px) ficava 2px menor que a linha real (8 do `py-2` + 16 da
// caixa de linha + 8 do `py-2` + 1 do `border-b` = 33px) — 5 linhas × 2px = 10px de salto
// visível ao trocar loading→success.
//
// Skeleton de lista de portal (portal-counts, BRIEF-011) — ao contrário do skeleton de
// tabela acima, era uma APROXIMAÇÃO BEST-EFFORT quando escrito: o componente real
// (`app-portal-card`/`PortalCardComponent`, entregue por AUT-6965 e em uso desde então)
// ainda não existia neste repositório, então não havia geometria real para calibrar
// (diferente do BRIEF-010, que mediu `getBoundingClientRect` do card de KPI real). Grade
// `grid-cols-[repeat(auto-fill,minmax(200px,1fr))]` e 8 linhas replicam a grade e a contagem
// do protótipo aprovado (`skelPortals`) — achado #2 do retry do product-designer: a versão
// anterior empilhava as linhas num `<div>` solto, sem grade, virando 1 coluna. Geometria
// ainda não recalibrada contra o card real; revisar numa TASK futura se houver salto
// visível loading→success. `auto-fill`, não `auto-fit`: com `auto-fit`, trilhas vazias
// colapsam e o `1fr` restante é redistribuído só entre as trilhas ocupadas — com 1 portal
// só, o card único esticava para a largura inteira da grade em vez de ficar alinhado à
// esquerda no seu `minmax(200px,1fr)` natural. `auto-fill` preserva trilhas vazias
// "fantasma" que absorvem esse espaço sobrando.
// `py-[12px] px-[16px]` (valor arbitrário) — o utility bare Tailwind equivalente (padding
// vertical nível 3, padding horizontal nível 4) colide com CSS legado `!important` de
// valor diferente (padding-top/padding-bottom de 1rem no nível vertical, padding de 1.5rem
// no nível horizontal — o legado zeraria os 12px/16px pretendidos aqui).
// `legacy-css-collisions.spec.ts` confirmou o achado (guardrail existente, não precisou de
// allowlist nova). IMPORTANTE: não grafar o nome bare desses 2 utilities por extenso neste
// arquivo — o `content` do Tailwind escaneia texto puro (lessons.md, "O `content` scanner é
// texto puro"), e citá-los reintroduziria as 2 classes (não usadas em nenhum template) no
// bundle.
//
// Skeleton de vitrine de logo (portals-to-contract, BRIEF-011, achado #3 do retry do
// product-designer — erro do brief original, não da implementação) — constante PRÓPRIA
// (`portalLogoSkeletonItems`), sem compartilhar `portalListSkeletonRows`/o layout de
// nome+contagem acima: SPEC-001 FR-001-020 [MUST] exige exibir esses portais "com o logo de
// cada um, sem nenhuma contagem associada e sem nenhuma ação de clique" — o skeleton de
// lista de portal (2 barras por linha) prometia visualmente uma contagem que a tela nunca
// mostra. O protótipo aprovado (linhas 486-493) usa uma vitrine `flex flex-wrap
// items-center gap:14px 28px` de blocos `height:40px;width:120px` — 1 barra por item
// (`h-10 w-[120px]`, sem barra secundária), 3 itens (contagem do protótipo).

@Component({
  selector: 'app-dashboard',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="aav-scope view-wrapper view-wrapper__dashboard">
      <div class="container">
        <div class="col-md-12">
          <div class="view-title">
            <h1 class="m-0 text-aav-title-large-strong text-aav-on-surface">
              {{ 'DASHBOARD.TITLE' | translate }}
            </h1>
            <p class="mt-[6px] mb-0 text-aav-body-medium text-aav-on-surface-variant">
              {{ 'DASHBOARD.SUB_TITLE' | translate }}
            </p>
          </div>

          <div class="mt-[1.25rem] space-y-5">
            <section
              data-block="kpis"
              [class]="kpisSectionClass"
              [attr.aria-label]="'DASHBOARD.KPIS.TITLE' | translate"
            >
              <div
                *ngIf="kpis.status === 'loading'"
                class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                role="status"
                aria-live="polite"
                aria-label="Carregando"
              >
                <div
                  *ngFor="let skeleton of kpiSkeletons; trackBy: trackByKpiSkeleton"
                  data-kpi-skeleton
                  class="rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div
                      class="h-3 w-[60%] rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                    ></div>
                    <div
                      class="h-9 w-9 rounded-aav-sm bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                    ></div>
                  </div>
                  <!-- 35px = altura da caixa de linha do <p> de valor real
                       (text-[30px] font-bold leading-[normal], BRIEF-009) — a barra do
                       skeleton (h-6, 24px) fica visualmente fina mas o wrapper reserva os
                       35px inteiros, para o card não saltar de altura ao trocar de estado
                       (medido: 102px loading / 113px success sem este wrapper). -->
                  <div class="mt-2 flex h-[35px] items-center">
                    <div
                      class="h-6 w-10 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                    ></div>
                  </div>
                </div>
              </div>

              <div
                *ngIf="kpis.status === 'success'"
                class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
              >
                <div
                  data-kpi="active"
                  class="rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <p
                      class="m-0 text-[13px] font-bold leading-[normal] uppercase tracking-wide text-aav-on-surface-variant"
                    >
                      {{ 'DASHBOARD.KPIS.ACTIVE' | translate }}
                    </p>
                    <span
                      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-aav-sm bg-aav-surface-container-low"
                    >
                      <aav-icon name="directions_car" class="text-aav-on-surface"></aav-icon>
                    </span>
                  </div>
                  <p
                    data-selectable
                    class="m-0 mt-2 text-[30px] font-bold leading-[normal] text-aav-on-surface"
                  >
                    {{ kpis.data?.total }}
                  </p>
                </div>
                <div
                  data-kpi="announced"
                  class="rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <p
                      class="m-0 text-[13px] font-bold leading-[normal] uppercase tracking-wide text-aav-on-surface-variant"
                    >
                      {{ 'DASHBOARD.KPIS.ANNOUNCED' | translate }}
                    </p>
                    <span
                      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-aav-sm bg-aav-feedback-green"
                    >
                      <aav-icon name="campaign" class="text-aav-on-feedback-green"></aav-icon>
                    </span>
                  </div>
                  <p
                    data-selectable
                    class="m-0 mt-2 text-[30px] font-bold leading-[normal] text-aav-on-feedback-green"
                  >
                    {{ kpis.data?.announced }}
                  </p>
                </div>
                <div
                  data-kpi="failure"
                  class="rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <p
                      class="m-0 text-[13px] font-bold leading-[normal] uppercase tracking-wide text-aav-on-surface-variant"
                    >
                      {{ 'DASHBOARD.KPIS.FAILURE' | translate }}
                    </p>
                    <span
                      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-aav-sm bg-aav-error/[0.12]"
                    >
                      <aav-icon name="warning" class="text-aav-error"></aav-icon>
                    </span>
                  </div>
                  <p
                    data-selectable
                    class="m-0 mt-2 text-[30px] font-bold leading-[normal] text-aav-error"
                  >
                    {{ kpis.data?.failure }}
                  </p>
                </div>
                <div
                  data-kpi="sold"
                  class="rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <p
                      class="m-0 text-[13px] font-bold leading-[normal] uppercase tracking-wide text-aav-on-surface-variant"
                    >
                      {{ 'DASHBOARD.KPIS.SOLD' | translate }}
                    </p>
                    <span
                      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-aav-sm bg-aav-feedback-orange"
                    >
                      <aav-icon name="task_alt" class="text-aav-on-feedback-orange"></aav-icon>
                    </span>
                  </div>
                  <p
                    data-selectable
                    class="m-0 mt-2 text-[30px] font-bold leading-[normal] text-aav-on-surface"
                  >
                    {{ kpis.data?.sold }}
                  </p>
                </div>
              </div>

              <p
                *ngIf="kpis.status === 'error'"
                role="alert"
                class="m-0 rounded-aav-sm border-[1px] border-[rgb(var(--aav-neutral-200))] bg-aav-surface p-[1rem] text-aav-body-medium text-aav-error"
              >
                {{ 'DASHBOARD.KPIS.ERROR' | translate }}
              </p>
            </section>

            <section
              data-block="portal-counts"
              *ngIf="portalCounts.status !== 'empty'"
              [class]="blockSurfaceClass"
            >
              <h2 class="mb-[1rem] text-[15px] font-bold leading-[32px] text-aav-on-surface">
                {{ 'DASHBOARD.PORTAL_COUNTS.TITLE' | translate }}
              </h2>

              <div
                *ngIf="portalCounts.status === 'loading'"
                data-portal-list-skeleton
                role="status"
                aria-live="polite"
                aria-label="Carregando"
                [class]="portalCountsGridClass"
              >
                <div
                  *ngFor="let row of portalListSkeletonRows; trackBy: trackByPortalListSkeletonRow"
                  data-portal-list-skeleton-row
                  class="flex items-center justify-between gap-[10px] py-[12px] px-[16px]"
                >
                  <div
                    class="h-[18px] w-20 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                  ></div>
                  <div
                    class="h-[18px] w-9 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                  ></div>
                </div>
              </div>

              <div *ngIf="portalCounts.status === 'success'" [class]="portalCountsGridClass">
                <app-portal-card
                  *ngFor="let item of portalCounts.data; trackBy: trackByPortalCard"
                  [name]="item.name"
                  [logo]="item.logo"
                  [count]="item.count"
                ></app-portal-card>
              </div>

              <p
                *ngIf="portalCounts.status === 'error'"
                role="alert"
                class="m-0 text-aav-body-medium text-aav-error"
              >
                {{ 'DASHBOARD.PORTAL_COUNTS.ERROR' | translate }}
              </p>
            </section>

            <div class="flex flex-col gap-4 lg:flex-row lg:gap-5">
              <section
                data-block="failed-ads"
                [class]="blockSurfaceClass + ' flex-1 flex flex-col'"
              >
                <div class="mb-[1rem] flex items-center justify-between gap-4">
                  <h2 class="m-0 text-[15px] font-bold leading-[32px] text-aav-on-surface">
                    {{ 'DASHBOARD.FAILED_ADS.TITLE' | translate }}
                  </h2>
                  <aav-button
                    variant="outlined-neutral"
                    size="sm"
                    (clicked)="onViewFailedAdsInList()"
                  >
                    {{ 'DASHBOARD.FAILED_ADS.VIEW_IN_LIST' | translate }}
                  </aav-button>
                </div>

                <div
                  *ngIf="failedAds.status === 'loading'"
                  data-table-skeleton
                  class="flex-1"
                  role="status"
                  aria-live="polite"
                  aria-label="Carregando"
                >
                  <ul class="m-0 list-none p-0">
                    <li
                      *ngFor="let row of tableSkeletonRows; trackBy: trackByTableSkeletonRow"
                      data-table-skeleton-row
                      class="grid grid-cols-[1fr_1fr_2fr] gap-2 border-b border-aav-outline py-2"
                    >
                      <div class="flex h-4 items-center">
                        <div
                          class="h-3.5 w-3/4 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                        ></div>
                      </div>
                      <div class="flex h-4 items-center">
                        <div
                          class="h-3.5 w-2/3 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                        ></div>
                      </div>
                      <div class="flex h-4 items-center">
                        <div
                          class="h-3.5 w-1/2 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                        ></div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div *ngIf="failedAds.status === 'success'" class="flex-1">
                  <div
                    class="grid grid-cols-[1fr_1fr_2fr] gap-2 border-b border-aav-outline pb-2 text-aav-label-medium-strong text-aav-on-surface-variant"
                  >
                    <span>{{ 'DASHBOARD.FAILED_ADS.COLUMN_VEHICLE' | translate }}</span>
                    <span>{{ 'DASHBOARD.FAILED_ADS.COLUMN_PORTAL' | translate }}</span>
                    <span>{{ 'DASHBOARD.FAILED_ADS.COLUMN_REASON' | translate }}</span>
                  </div>

                  <ul class="m-0 list-none p-0">
                    <li
                      *ngFor="let item of failedAds.data; trackBy: trackByFailedAd"
                      class="grid grid-cols-[1fr_1fr_2fr] gap-2 border-b border-aav-outline py-2 text-aav-body-small"
                    >
                      <span
                        data-cell="plate"
                        data-selectable
                        class="text-[rgb(var(--aav-stone-900))]"
                        >{{ item.plate }}</span
                      >
                      <span
                        data-cell="portal"
                        data-selectable
                        class="text-[rgb(var(--aav-stone-900))]"
                        >{{ item.portal }}</span
                      >
                      <span data-cell="reason" data-selectable class="text-[#B7231F]">{{
                        item.reason
                      }}</span>
                    </li>
                  </ul>
                </div>

                <div *ngIf="failedAds.status === 'empty'" [class]="cardMessageWrapperClass">
                  <p class="m-0 text-aav-body-medium text-aav-on-surface-variant">
                    {{ 'DASHBOARD.FAILED_ADS.EMPTY' | translate }}
                  </p>
                </div>

                <div *ngIf="failedAds.status === 'error'" [class]="cardMessageWrapperClass">
                  <p role="alert" class="m-0 text-aav-body-medium text-aav-error">
                    {{ 'DASHBOARD.FAILED_ADS.ERROR' | translate }}
                  </p>
                </div>
              </section>

              <section
                data-block="recent-ads"
                [class]="blockSurfaceClass + ' flex-1 flex flex-col'"
              >
                <div class="mb-[1rem] flex items-center justify-between gap-4">
                  <h2 class="m-0 text-[15px] font-bold leading-[32px] text-aav-on-surface">
                    {{ 'DASHBOARD.RECENT_ADS.TITLE' | translate }}
                  </h2>
                  <aav-button
                    variant="outlined-neutral"
                    size="sm"
                    (clicked)="onViewRecentAdsInList()"
                  >
                    {{ 'DASHBOARD.RECENT_ADS.VIEW_IN_LIST' | translate }}
                  </aav-button>
                </div>

                <div
                  *ngIf="recentAds.status === 'loading'"
                  data-table-skeleton
                  class="flex-1"
                  role="status"
                  aria-live="polite"
                  aria-label="Carregando"
                >
                  <ul class="m-0 list-none p-0">
                    <li
                      *ngFor="let row of tableSkeletonRows; trackBy: trackByTableSkeletonRow"
                      data-table-skeleton-row
                      class="grid grid-cols-[1fr_1fr_2fr] gap-2 border-b border-aav-outline py-2"
                    >
                      <div class="flex h-4 items-center">
                        <div
                          class="h-3.5 w-3/4 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                        ></div>
                      </div>
                      <div class="flex h-4 items-center">
                        <div
                          class="h-3.5 w-2/3 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                        ></div>
                      </div>
                      <div class="flex h-4 items-center">
                        <div
                          class="h-3.5 w-1/2 rounded bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                        ></div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div *ngIf="recentAds.status === 'success'" class="flex-1">
                  <div
                    class="grid grid-cols-[1fr_1fr_2fr] gap-2 border-b border-aav-outline pb-2 text-aav-label-medium-strong text-aav-on-surface-variant"
                  >
                    <span>{{ 'DASHBOARD.RECENT_ADS.COLUMN_PLATE' | translate }}</span>
                    <span>{{ 'DASHBOARD.RECENT_ADS.COLUMN_PORTAL' | translate }}</span>
                    <span>{{ 'DASHBOARD.RECENT_ADS.COLUMN_LINK' | translate }}</span>
                  </div>

                  <ul class="m-0 list-none p-0">
                    <li
                      *ngFor="let item of recentAds.data; trackBy: trackByRecentAd"
                      class="grid grid-cols-[1fr_1fr_2fr] items-center gap-2 border-b border-aav-outline py-2 text-aav-body-small"
                    >
                      <span
                        data-cell="plate"
                        data-selectable
                        class="text-[rgb(var(--aav-stone-900))]"
                        >{{ item.plate }}</span
                      >
                      <span
                        data-cell="portal"
                        data-selectable
                        class="text-[rgb(var(--aav-stone-900))]"
                        >{{ item.portal }}</span
                      >
                      <a
                        *ngIf="item.hasValidLink"
                        data-cell="link"
                        data-selectable
                        class="min-w-0 truncate text-aav-inverse-primary hover:underline"
                        [attr.href]="item.url"
                        [attr.title]="item.url"
                        [attr.aria-label]="
                          'DASHBOARD.RECENT_ADS.LINK_ARIA_LABEL'
                            | translate: { plate: item.plate, portal: item.portal }
                        "
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ item.url }}
                      </a>
                      <span *ngIf="!item.hasValidLink" data-cell="link" data-selectable>{{
                        item.url || '—'
                      }}</span>
                    </li>
                  </ul>
                </div>

                <div *ngIf="recentAds.status === 'empty'" [class]="cardMessageWrapperClass">
                  <p class="m-0 text-aav-body-medium text-aav-on-surface-variant">
                    {{ 'DASHBOARD.RECENT_ADS.EMPTY' | translate }}
                  </p>
                </div>

                <div *ngIf="recentAds.status === 'error'" [class]="cardMessageWrapperClass">
                  <p role="alert" class="m-0 text-aav-body-medium text-aav-error">
                    {{ 'DASHBOARD.RECENT_ADS.ERROR' | translate }}
                  </p>
                </div>
              </section>
            </div>

            <section
              data-block="portals-to-contract"
              *ngIf="portalsToContract.status !== 'empty'"
              [class]="blockSurfaceClass"
            >
              <h2 class="mb-[4px] text-[15px] font-bold leading-[32px] text-aav-on-surface">
                {{ 'DASHBOARD.PORTALS_TO_CONTRACT.TITLE' | translate }}
              </h2>
              <p class="mb-[18px] text-aav-body-small text-aav-on-surface-variant">
                {{ 'DASHBOARD.PORTALS_TO_CONTRACT.SUBTITLE' | translate }}
              </p>

              <div
                *ngIf="portalsToContract.status === 'loading'"
                data-portal-logo-skeleton
                role="status"
                aria-live="polite"
                aria-label="Carregando"
                [class]="portalsToContractShowcaseClass"
              >
                <div
                  *ngFor="
                    let item of portalLogoSkeletonItems;
                    trackBy: trackByPortalLogoSkeletonItem
                  "
                  data-portal-logo-skeleton-item
                  class="h-10 w-[120px] rounded-aav-sm bg-[rgb(var(--aav-neutral-200))] animate-aav-pulse"
                ></div>
              </div>

              <div
                *ngIf="portalsToContract.status === 'success'"
                [class]="portalsToContractShowcaseClass"
              >
                <app-portal-card
                  *ngFor="let item of portalsToContract.data; trackBy: trackByPortalCard"
                  [name]="item.name"
                  [logo]="item.logo"
                ></app-portal-card>
              </div>

              <p
                *ngIf="portalsToContract.status === 'error'"
                role="alert"
                class="m-0 text-aav-body-medium text-aav-error"
              >
                {{ 'DASHBOARD.PORTALS_TO_CONTRACT.ERROR' | translate }}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class DashboardComponent implements OnInit {
  readonly blockSurfaceClass = BLOCK_SURFACE_CLASS;
  readonly cardMessageWrapperClass = CARD_MESSAGE_WRAPPER_CLASS;
  readonly portalCountsGridClass = PORTAL_COUNTS_GRID_CLASS;
  readonly portalsToContractShowcaseClass = PORTALS_TO_CONTRACT_SHOWCASE_CLASS;

  // `relative` é classe fixa, não condicional: os 4 skeletons do estado `loading` ocupam
  // espaço no fluxo normal, no mesmo grid do estado `success` (BRIEF-010) — nenhum estado
  // da section tem filho posicionado `absolute` que precise de `min-h-[120px]` para ser
  // ancorado.
  readonly kpisSectionClass = 'relative';

  // 4 posições fixas (paridade com os 4 KPIs reais: active/announced/failure/sold) — o
  // valor em si não é usado no template, só a contagem, via `*ngFor`/`trackByKpiSkeleton`.
  readonly kpiSkeletons = [0, 1, 2, 3];

  // `DASHBOARD_LIST_LIMIT` posições (failed-ads/recent-ads, BRIEF-011) — mesma constante
  // usada no `limit` das 2 chamadas de API (paridade geométrica: skeleton com o mesmo
  // número de linhas do estado `success`). O valor em si não é usado no template, só a
  // contagem, via `*ngFor`/`trackByTableSkeletonRow`.
  readonly tableSkeletonRows = Array.from({ length: DASHBOARD_LIST_LIMIT }, (_, index) => index);

  // 8 posições fixas (portal-counts, BRIEF-011 retry — achado #2) — contagem do protótipo
  // aprovado (`skelPortals`), ver comentário "Skeleton de lista de portal" acima: geometria
  // ainda não recalibrada contra o `app-portal-card` real (AUT-6965).
  readonly portalListSkeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];

  // 3 posições fixas (portals-to-contract, BRIEF-011 retry — achado #3) — contagem do
  // protótipo aprovado (vitrine de logos, linhas 486-493), ver comentário "Skeleton de
  // vitrine de logo" acima (FR-001-020: sem contagem, sem ação de clique).
  readonly portalLogoSkeletonItems = [0, 1, 2];

  kpis: DashboardBlockState<DashboardTotals> = { status: 'loading', data: null };
  portalCounts: DashboardBlockState<DashboardPortalCardView[]> = { status: 'loading', data: null };
  failedAds: DashboardBlockState<DashboardFailedIntegration[]> = { status: 'loading', data: null };
  portalsToContract: DashboardBlockState<DashboardPortalCardView[]> = {
    status: 'loading',
    data: null,
  };
  recentAds: DashboardBlockState<DashboardAnnouncedItemView[]> = { status: 'loading', data: null };

  private dashboardService: DashboardService | null = null;
  private vehicleService: VehicleService | null = null;
  private navigationService: NavigationService | null = null;
  private errorHandler: ErrorHandler | null = null;

  // Memoiza a promise (não o resultado) para que loadPortalCounts/loadPortalsToContract,
  // disparados juntos em ngOnInit, aguardem a mesma requisição em voo (DEC-002-005).
  private portalsCatalogPromise: Promise<Portal[]> | null = null;

  // `ChangeDetectorRef` é dependência de view do Angular core (não service da ponte
  // NgUpgrade) — exceção legítima à regra do CLAUDE.md contra injeção no construtor, que
  // veda apenas services obtidos via `getAngularService()`.
  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.dashboardService = getAngularService<DashboardService>(DashboardService);
    this.vehicleService = getAngularService<VehicleService>(VehicleService);
    this.navigationService = getAngularService<NavigationService>(NavigationService);
    this.errorHandler = getAngularService<ErrorHandler>(ErrorHandler);

    // Paridade com panel.dashboard.controller.js:9-13 (DEC-002-002, FR-001-006).
    const widget = document.getElementById('md-app-widget');
    if (widget) {
      widget.style.display = 'block';
    }

    void this.loadKpis();
    void this.loadPortalCounts();
    void this.loadFailedAds();
    void this.loadPortalsToContract();
    void this.loadRecentAds();
  }

  onViewFailedAdsInList(): void {
    this.navigationService?.goToState(AppState.PanelVehicleList);
  }

  onViewRecentAdsInList(): void {
    this.navigationService?.goToState(AppState.PanelVehicleList);
  }

  trackByFailedAd(_index: number, item: DashboardFailedIntegration): string {
    return `${item.plate}-${item.portal}`;
  }

  trackByRecentAd(_index: number, item: DashboardAnnouncedItemView): string {
    return `${item.plate}-${item.portal}`;
  }

  trackByKpiSkeleton(index: number): number {
    return index;
  }

  trackByTableSkeletonRow(index: number): number {
    return index;
  }

  trackByPortalListSkeletonRow(index: number): number {
    return index;
  }

  trackByPortalLogoSkeletonItem(index: number): number {
    return index;
  }

  trackByPortalCard(_index: number, item: DashboardPortalCardView): number {
    return item.id;
  }

  private async loadKpis(): Promise<void> {
    this.kpis = { status: 'loading', data: null };
    this.changeDetectorRef.markForCheck();
    try {
      const result = await this.dashboardService!.getGraphData({ type: 'totals' });
      this.kpis = { status: 'success', data: result };
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      this.kpis = { status: 'error', data: null };
      this.changeDetectorRef.markForCheck();
      this.reportBlockFailure('totals', error);
    }
  }

  private async loadFailedAds(): Promise<void> {
    this.failedAds = { status: 'loading', data: null };
    this.changeDetectorRef.markForCheck();
    try {
      const result = await this.dashboardService!.getGraphData({
        type: 'latest_failed',
        limit: DASHBOARD_LIST_LIMIT,
      });
      const items = result ?? [];
      this.failedAds = { status: items.length === 0 ? 'empty' : 'success', data: items };
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      this.failedAds = { status: 'error', data: null };
      this.changeDetectorRef.markForCheck();
      this.reportBlockFailure('latest_failed', error);
    }
  }

  // Catálogo de portais (id/name/logo) memoizado — reaproveitado pelos blocos 2 e 4
  // (DEC-002-005). Uma segunda chamada concorrente aguarda a mesma promise em voo, em vez
  // de disparar uma segunda requisição a vehicleService.getPortals().
  private getPortalsCatalog(): Promise<Portal[]> {
    if (!this.portalsCatalogPromise) {
      this.portalsCatalogPromise = this.vehicleService!.getPortals();
    }
    return this.portalsCatalogPromise;
  }

  // Une a contagem/lista bruta do DashboardService (id/portal/count?) com o catálogo de
  // portais (id/name/logo) — portal ausente no catálogo usa o nome do próprio payload de
  // dashboard e fica sem logo (DEC-002-005).
  private mergeWithPortalCatalog(
    rows: Array<{ id: number; portal: string; count?: number }>,
    catalog: Portal[]
  ): DashboardPortalCardView[] {
    const byId = new Map(catalog.map((p) => [p.id, p]));
    return rows.map((row) => {
      const found = byId.get(row.id);
      return {
        id: row.id,
        name: found?.name ?? row.portal,
        logo: found?.logo ?? null,
        count: row.count ?? null,
      };
    });
  }

  private async loadPortalCounts(): Promise<void> {
    this.portalCounts = { status: 'loading', data: null };
    this.changeDetectorRef.markForCheck();
    try {
      const [rows, catalog] = await Promise.all([
        this.dashboardService!.getGraphData({ type: 'announced_by_portal' }),
        this.getPortalsCatalog(),
      ]);
      const items = this.mergeWithPortalCatalog(rows ?? [], catalog).sort(
        (a, b) => (b.count ?? 0) - (a.count ?? 0)
      );
      this.portalCounts = { status: items.length === 0 ? 'empty' : 'success', data: items };
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      this.portalCounts = { status: 'error', data: null };
      this.changeDetectorRef.markForCheck();
      this.reportBlockFailure('announced_by_portal', error);
    }
  }

  private async loadPortalsToContract(): Promise<void> {
    this.portalsToContract = { status: 'loading', data: null };
    this.changeDetectorRef.markForCheck();
    try {
      const [rows, catalog] = await Promise.all([
        this.dashboardService!.getGraphData({ type: 'portals_not_contracted' }),
        this.getPortalsCatalog(),
      ]);
      const items = this.mergeWithPortalCatalog(rows ?? [], catalog);
      this.portalsToContract = { status: items.length === 0 ? 'empty' : 'success', data: items };
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      this.portalsToContract = { status: 'error', data: null };
      this.changeDetectorRef.markForCheck();
      this.reportBlockFailure('portals_not_contracted', error);
    }
  }

  private isValidExternalLink(url?: string): boolean {
    return typeof url === 'string' && /^https?:\/\//i.test(url);
  }

  private async loadRecentAds(): Promise<void> {
    this.recentAds = { status: 'loading', data: null };
    this.changeDetectorRef.markForCheck();
    try {
      const result = await this.dashboardService!.getGraphData({
        type: 'latest_announced',
        limit: DASHBOARD_LIST_LIMIT,
      });
      const items = (result ?? []).map((item) => ({
        ...item,
        hasValidLink: this.isValidExternalLink(item.url),
      }));
      this.recentAds = { status: items.length === 0 ? 'empty' : 'success', data: items };
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      this.recentAds = { status: 'error', data: null };
      this.changeDetectorRef.markForCheck();
      this.reportBlockFailure('latest_announced', error);
    }
  }

  // Única implementação neste arquivo (DEC-002-003) — TASK-002-002/003/004/005 apenas chamam,
  // cada uma no próprio catch de bloco.
  private reportBlockFailure(block: string, error: unknown): void {
    const status = (error as { status?: number })?.status;
    const code = (error as { data?: { error?: string } })?.data?.error;
    const safeError = new Error(
      `Dashboard block "${block}" failed to load (status=${status ?? 'n/a'}, code=${code ?? 'n/a'})`
    );
    this.errorHandler?.handleError(safeError);
  }
}
